import { IOfferCreateAddress, IOfferCreateItem, IOfferCreateRequestDto } from '@r1-frontend/api-domru/full-buy/v2/agreement/offer-create';
import { IAddressProps } from '@r1-frontend/entities/ContactAddress/selectors/selectAddressParams';

import { isServerSide } from '@r1-frontend/shared/helpers/ssr';
import { TSalePackage } from '@r1-frontend/shared/types/tariff/salePackage/TSalePackage';

import { getCitiesTariffs, IGetCitiesTariffsQueryParams } from '~/src/api/content/cities/tariffs';
import { DEALER_UID } from '~/src/constants/sessionStorageKeys';
import { getProductsCosts } from '~/src/features/full-buy/functions';
import { TUnifiedAddressData } from '~/src/features/full-buy/store/initialState';

import { IFullBuyTariff, tariffTransformer } from './tariffTransformer';
import { TProduct } from './transformShopItem';

interface IRequestBody {
    fio: string,
    phone: string,
    tariffId: number,
    productsByProps: IOfferCreateItem[],
    summaryCheckInfo: string,
    promoCode: string,
    analytics_id: string,
}

/**
 * Возвращает true если браузер поддерживает webp
 * Должен использоваться только на стороне браузера
 */
const isWebp = async(): Promise<boolean> => {
    if (isServerSide()) {
        throw new Error('Проверка isWebp(), вызвана на стороне сервера (SSR)!');
    }

    return new Promise<boolean>(resolve => {
        const image = new Image();
        image.onerror = () => resolve(false);
        image.onload = () => resolve(image.width === 1);
        image.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
    }).catch(() => false);
};

// Данные по пакетам тарифов
export const fetchTariffPackages = async(providerId: number, queryParams: IGetCitiesTariffsQueryParams, forCottage = false): Promise<IFullBuyTariff[] | null> => {
    const resp = await getCitiesTariffs(providerId, queryParams);
    const retrieveTariffsFunction = forCottage ? getOnlyCottageTariffPackages : getOnlyCityTariffPackages;

    if (resp.isSuccess) {
        const { bundles, mono } = resp.payload;

        const bundlePackages = Object.values(bundles || {});
        const tariffs = retrieveTariffsFunction([...mono, ...bundlePackages]);

        if (!tariffs) {
            return null;
        }

        const isSupportsWebp = await isWebp();

        // @todo move transformer to the initialization action
        return tariffs?.map(tariff => tariffTransformer(tariff, isSupportsWebp)) || null;
    }

    return null;
};

// Получаем только тарифы для города
export const getOnlyCityTariffPackages = (tariffPackages: TSalePackage[] | null): TSalePackage[] | undefined => {
    return tariffPackages?.filter((pack) => {
        const isCottage = 'salePackage2in1' in pack ? pack.salePackage2in1.is_cottage : pack.is_cottage;
        return !isCottage;
    });
};

export const getOnlyCottageTariffPackages = (tariffPackages: TSalePackage[] | null): TSalePackage[] | undefined => {
    return tariffPackages?.filter((pack) => {
        return 'salePackage2in1' in pack ? pack.salePackage2in1.is_cottage : pack.is_cottage;
    });
};

interface ICheckInfoProps {
    promoPrice: number | null,
    price: number | null,
    products: TProduct[],
    connectPrice: number,
    oncePaymentSum: number,
    monthlyPaymentSum: number,
}

/**
 * Собирает информацию для комментария при создании договора
 */
export const getSummaryCheckInfo = ({
    promoPrice,
    price,

    products,
    connectPrice,
    oncePaymentSum,
    monthlyPaymentSum,
}: ICheckInfoProps) => {
    const showPrice = promoPrice || price;
    const tariff = showPrice ? `, тариф ${showPrice}` : '';
    const productsCost = getProductsCosts(products);
    const oncePaymentEquipment = `, оборудование ${productsCost.once}`;
    const connection = `, подключение ${connectPrice}`;
    const monthlyPaymentEquipment = `, оборудование ${productsCost.monthly}`;

    return `Ежемесячно ${monthlyPaymentSum}` +
        tariff +
        monthlyPaymentEquipment +
        '. ' +
        `Разово ${oncePaymentSum}` +
        connection +
        oncePaymentEquipment +
        '. ' +
        `Итого: ${oncePaymentSum + monthlyPaymentSum}`;
};

// собираем данные для отправки запроса
export const createOfferRequestBody = (
    address: IAddressProps | null,
    unifiedAddress: TUnifiedAddressData | undefined,
    {
        fio,
        phone,
        tariffId,
        productsByProps,
        summaryCheckInfo,
        promoCode,
        analytics_id,
    }: IRequestBody,
): IOfferCreateRequestDto => {
    const dealerUid = window.sessionStorage.getItem(DEALER_UID) ?? undefined;

    let addressData: IOfferCreateAddress = { companyId: 0, office: '' };

    if (address) {
        addressData = {
            houseId: address.house.value,
            office: address.flat,
            companyId: address.companyId,
        };
    }

    if (unifiedAddress) {
        addressData = {
            unifiedHouseId: unifiedAddress.address.unifiedHouseId,
            office: unifiedAddress.address.flat,
            companyId: 0,
        };
    }

    const result: IOfferCreateRequestDto = {
        address: addressData,
        personalInformation: {
            clientName: fio,
            clientPhone: phone,
        },
        tariff: {
            tariffId,
        },
        items: productsByProps,
        comment: summaryCheckInfo,
        promocodeName: promoCode,
        analyticsId: analytics_id,
    };

    if (dealerUid) {
        result.managerId = Number(dealerUid);
        window.sessionStorage.removeItem(DEALER_UID);
    }

    return result;
};
